import {
	CalendarOutlined,
	CheckCircleTwoTone,
	EditOutlined,
	EnvironmentOutlined,
	GlobalOutlined,
	MailOutlined,
	PhoneOutlined,
	PullRequestOutlined,
	SettingOutlined,
	ShareAltOutlined,
	UserOutlined
} from '@ant-design/icons';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Layout, Modal, Row, Skeleton, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IoLocationSharp } from 'react-icons/io5';
import { MdOutlineStairs } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { SocialIcon } from 'react-social-icons';
import defaultCover from '../../../assets/images/company-default-cover.jpg';
import companyDefaultLogo from '../../../assets/images/company-default-logo.png';
import { ButtonType, ContentBox, TAButton, TitledValue } from '../../../components';
import { CommentBox } from '../../../components/comments/comment-box';
import { CommentItem } from '../../../components/comments/comment-item';
import JobDescriptionList from '../../../components/dashboard/jobs/job-description-list';
import { AvatarWindow, BoxTitle, CoverPicture, Title, TitleLink } from '../../../components/dashboard/styles';
import { SkeletonList } from '../../../components/skeleton-list/skeleton-list';
import AuthenticationContext, { useAuth } from '../../../contexts/authentication.context';
import authService from '../../../services/auth.service';
import companyService from '../../../services/company.service';
import jobsService from '../../../services/jobs.service';
import { notifyError } from '../../../utils/notification';
import { locationToAddress, camelCaseToWord } from '../../../utils/strings';
import { verificationMapper } from '../../../utils/verification-mapper';
import TeamMembers from '../team-members/team-members';
import classes from './company-view.module.css';
import { AppRoutes } from '../../../enums';
import { BsBuilding } from 'react-icons/bs';
import { AiFillCheckCircle } from 'react-icons/ai';
import { GiSandsOfTime } from 'react-icons/gi';
import { useTheme } from 'styled-components';

export const urlPattern =
	/(https?:\/\/)(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)/;

const { Content } = Layout;

export const CompanyView: React.FC<any> = ({ backOffice }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [newCommentLoading, setNewCommentLoading] = useState<boolean>(false);
	const [company, setCompany] = useState<any>(null);
	const [comments, setComments] = useState<any>([]);

	let { companyId } = useParams();
	const theme = useTheme();

	const userAvatarMap = {};
	const user = authService.getUserData();

	const mountLogic = async () => {
		if (!companyId && !backOffice) {
			companyId = authService.getUserData().company.id;
		}
		let company, comments;
		try {
			company = await jobsService.getCompany(companyId);
			setCompany(company);
			setLoading(false);
		} catch (e) {
			notifyError('Error while fetching company data, Please try again later.');
		}
		if (backOffice) {
			try {
				comments = await companyService.getCompanyComments(companyId);

				const commentsList = comments.reverse();
				setLoading(false);
				setComments(
					commentsList.map(c => {
						if (c.createdBy._id === user._id) {
							c.createdBy.avatarBgColor = user.avatarBgColor;
						} else {
							if (userAvatarMap[c.createdBy._id])
								c.createdBy.avatarBgColor = userAvatarMap[c.createdBy._id];
							else {
								userAvatarMap[c.createdBy._id] = c.createdBy.avatarBgColor = '#4ADCAA';
							}
						}
						return c;
					})
				);

				if (company === null) throw new Error();
			} catch (e) {
				//handle promise error.
				notifyError('Error while fetching company comments data, Please try again later.');
				return;
			}
		}

		company.jobs = company.jobs.reverse();
	};

	const handleAddComment = async (comment, attachment, callback) => {
		setNewCommentLoading(true);
		try {
			const newComment = await companyService.commentOnCompany(company._id, comment, attachment);
			const commentsList = [
				{
					...newComment,
					createdBy: {
						...user,
						id: user.userId
					}
				},
				...comments
			];
			setComments(commentsList);
			callback();
		} catch (e: any) {
			notifyError(e.message);
		} finally {
			setNewCommentLoading(false);
		}
	};

	useEffect(() => {
		mountLogic();
	}, []);

	const fixCompanyWebsite = url => {
		if (urlPattern.test(url)) return url;
		return 'https://' + url;
	};

	let location = (company && company.location && locationToAddress(company.location)) || null;

	return (
		<Content className="company-view-wrap">
			<CoverPicture src={company?.cover ? company.cover : defaultCover} />

			<Content className="company-view p-2 mx-auto">
				<Row
					gutter={20}
					className="company-view__info py-3 d-flex flex-column flex-lg-row align-items-center align-items-lg-end justify-content-center justify-content-lg-start"
					style={{
						backgroundColor: loading ? '#ffffff' : undefined,
						padding: loading ? '1.5rem 1rem' : 0,
						position: 'relative'
					}}
				>
					<Skeleton active loading={loading} avatar paragraph={{ rows: 2 }}>
						{company ? (
							<Fragment>
								<Col xs={24} lg={4} className="d-flex justify-content-center justify-content-md-center">
									<AvatarWindow src={company.logo || companyDefaultLogo} alt="avatar" size={140} />
								</Col>

								<Col
									xs={24}
									lg={20}
									className={`${classes.CompanyInfo} d-flex flex-column align-items-center align-items-lg-stretch justify-content-around pb-lg-4`}
								>
									<TitleLink to={location?.pathname} size={36} lineHeight={50}>
										<span className="text-center text-md-start">
											{company.name}
											{backOffice ? (
												verificationMapper(theme)[company.joinStatus]
											) : (
												<Tooltip title={company.verified ? 'Verified' : 'Pending verification'}>
													{company.verified ? (
														<AiFillCheckCircle color={theme.TA_GREEN_BLUE} />
													) : (
														<GiSandsOfTime color={theme.TA_WARNING} />
													)}
												</Tooltip>
											)}
										</span>
									</TitleLink>
								</Col>

								<Col className={classes.EditButton} lg={6}>
									<TAButton btntype={ButtonType.PRIMARY}>
										<Link to={AppRoutes.EDIT_COMPANY_PROFILE.replace(/:companyId/, company?.id)}>
											<EditOutlined /> Edit Profile
										</Link>
									</TAButton>
								</Col>
							</Fragment>
						) : null}
					</Skeleton>
				</Row>

				<Row gutter={15}>
					<Col xs={24} lg={18}>
						<ContentBox padding={0} minHeight={100}>
							<Skeleton active loading={loading} paragraph={{ rows: 4 }}>
								<BoxTitle>Bio</BoxTitle>
								<p style={{ padding: '1.5rem 1rem', fontSize: '16px' }}>
									{company?.bio || 'Company has no bio for the moment'}
								</p>
							</Skeleton>
						</ContentBox>

						{backOffice ? (
							<ContentBox padding={0}>
								<BoxTitle>Active Team Members</BoxTitle>
								<TeamMembers companyId={company?._id} />
							</ContentBox>
						) : null}

						<ContentBox padding={0}>
							<BoxTitle>Vacancies</BoxTitle>
							{company && (
								<JobDescriptionList
									loading={loading}
									jobs={company?.jobs}
									companyLogo={company?.logo}
									companyLocation={company?.location}
								/>
							)}
						</ContentBox>

						{backOffice ? (
							<AuthenticationContext.Consumer>
								{({ user }) => (
									<ContentBox padding={0}>
										<BoxTitle>Notes</BoxTitle>
										<div className="p-3">
											<CommentBox
												commenter={user}
												onSaveComment={handleAddComment}
												loading={newCommentLoading}
											/>

											{loading ? (
												<SkeletonList itemsCount={4} />
											) : comments && comments.length ? (
												<div
													style={{
														minHeight: '300px',
														maxHeight: '550px',
														overflow: 'auto'
													}}
												>
													{comments.map(comment => (
														<div
															style={{
																paddingTop: '1px',
																paddingBottom: '10px',
																paddingLeft: '5px',
																borderRadius: '5px'
															}}
														>
															<CommentItem
																attachments={comment.attachments}
																comment={comment.content}
																commenter={comment.createdBy}
																editable={
																	backOffice
																		? true
																		: comment.createdBy.id === user.userId
																}
																commentDate={comment.createdAt}
																onDelete={() => {
																	Modal.confirm({
																		title: 'Are you sure you want to delete this comment',
																		okButtonProps: { type: 'primary' },
																		onOk: async () => {
																			let deleted =
																				await companyService.deleteCompanyComment(
																					comment._id
																				);
																			if (deleted) {
																				setComments(
																					comments.filter(
																						c => c._id != comment._id
																					)
																				);
																			}
																		}
																	});
																}}
																onEdit={async (val, callback) => {
																	let updated =
																		await companyService.editCompanyComment(
																			comment._id,
																			val
																		);
																	if (updated) {
																		setComments(
																			comments.map(c => {
																				if (c._id === comment._id)
																					c.content = val;
																				return c;
																			})
																		);

																		callback();
																	}
																}}
															/>
														</div>
													))}
												</div>
											) : (
												<p className="mt-3">No comments yet</p>
											)}
										</div>
									</ContentBox>
								)}
							</AuthenticationContext.Consumer>
						) : null}
					</Col>
					<Col xs={24} lg={6}>
						<ContentBox padding={0} minHeight={300}>
							<Skeleton active loading={loading} paragraph={{ rows: 3 }}>
								{company && (
									<Fragment>
										<BoxTitle className="d-flex align-items-center">
											<Title text="About Company" iconType="info-circle" />
										</BoxTitle>
										<div
											style={{
												padding: '.5rem 1rem',
												fontSize: '10px',
												display: 'flex',
												flexDirection: 'column'
											}}
										>
											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<EnvironmentOutlined /> <span className="ms-2">Location:</span>
													</span>
												}
												layout="vertical"
												marginBottom={12}
											>
												<span style={{ marginLeft: '20px' }}>
													{location || 'NA'}{' '}
													{urlPattern.test(company.location.area) ? (
														<a href={company.location.area} target="_blank">
															<IoLocationSharp size={25} />
														</a>
													) : (
														''
													)}
												</span>
											</TitledValue>

											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<BsBuilding /> <span className="ms-2">Type:</span>
													</span>
												}
												layout="vertical"
												marginBottom={12}
											>
												<span style={{ marginLeft: '20px', display: 'block' }}>
													{company.type ?? 'NA'}
												</span>
											</TitledValue>
											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<SettingOutlined /> <span className="ms-2">Industry:</span>
													</span>
												}
												layout="vertical"
												marginBottom={12}
											>
												<span style={{ marginLeft: '20px', display: 'block' }}>
													{company.industry && company.industry.length
														? company.industry.join(', ')
														: 'NA'}
												</span>
											</TitledValue>
											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<CalendarOutlined /> <span className="ms-2">Founded At:</span>
													</span>
												}
												marginBottom={12}
											>
												<span style={{ marginLeft: '20px' }}>
													{company.foundationDate
														? dayjs(company.foundationDate).format('MMMM D, YYYY')
														: 'NA'}
												</span>
											</TitledValue>

											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<GlobalOutlined /> <span className="ms-2">Website:</span>
													</span>
												}
												marginBottom={12}
												// textColor={theme.TA_GREEN}
												layout="vertical"
											>
												{company.onlinePresence?.website ? (
													<a
														href={fixCompanyWebsite(company.onlinePresence?.website)}
														style={{ color: theme.TA_GREEN, marginLeft: '20px' }}
														target="_blank"
													>
														{company.onlinePresence?.website}
													</a>
												) : (
													<span
														style={{
															color: theme.TA_GREEN,
															marginLeft: '20px',
															display: 'inline-block'
														}}
													>
														NA
													</span>
												)}
											</TitledValue>

											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<FontAwesomeIcon icon={faBuilding} />{' '}
														<span className="ms-2">Company Size:</span>
													</span>
												}
												marginBottom={12}
												layout="vertical"
											>
												<span style={{ marginLeft: '20px' }}>
													{company.size ? `${company.size} Employees` : 'NA'}
												</span>
											</TitledValue>
											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<MdOutlineStairs size={20} />{' '}
														<span className="ms-1">Company Scale:</span>
													</span>
												}
												marginBottom={12}
												layout="vertical"
											>
												<span style={{ marginLeft: '25px' }}>
													{company.scale ? `${camelCaseToWord(company.scale)}` : 'NA'}
												</span>
											</TitledValue>
											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<PullRequestOutlined />
														<span className="ms-2">Referred By:</span>
													</span>
												}
												marginBottom={12}
												layout="vertical"
											>
												<span style={{ marginLeft: '20px' }}>
													{company.referral ? company.referral : 'NA'}
												</span>
											</TitledValue>

											<TitledValue
												desc={
													<span className="d-flex align-items-center">
														<ShareAltOutlined />{' '}
														<span className="ms-2">Online Presnece:</span>
													</span>
												}
												marginBottom={12}
												layout="vertical"
											>
												<span className="d-inline-block mt-2" style={{ marginLeft: '20px' }}>
													{company.onlinePresence?.facebookProfile && (
														<SocialIcon
															url={company.onlinePresence?.facebookProfile}
															target="blank"
															className="mx-2 mb-2"
														/>
													)}
													{company.onlinePresence?.linkedinProfile && (
														<SocialIcon
															url={company.onlinePresence?.linkedinProfile}
															target="blank"
															className="mx-2 mb-2"
														/>
													)}
													{company.onlinePresence?.twitterProfile && (
														<SocialIcon
															url={company.onlinePresence?.twitterProfile}
															target="blank"
															className="mx-2 mb-2"
														/>
													)}

													{company.onlinePresence?.instagramProfile && (
														<SocialIcon
															url={company.onlinePresence?.instagramProfile}
															target="blank"
															className="mx-2 mb-2"
														/>
													)}
												</span>
											</TitledValue>
										</div>
									</Fragment>
								)}
							</Skeleton>
						</ContentBox>

						{backOffice && (
							<ContentBox padding={0} minHeight={300}>
								<Skeleton active loading={loading} paragraph={{ rows: 3 }}>
									{company && (
										<Fragment>
											<BoxTitle className="d-flex align-items-center">
												<Title text="Contact" iconType="contacts" />
											</BoxTitle>

											<div style={{ padding: '.5rem 1rem', fontSize: '10px' }}>
												<TitledValue
													desc={
														<span className="d-flex align-items-center">
															<PhoneOutlined />{' '}
															<span className="ms-2">Company Phone:</span>
														</span>
													}
													layout="vertical"
													display="block"
													marginBottom={12}
												>
													{company.phone ? (
														<span style={{ marginLeft: '20px' }}>{company.phone}</span>
													) : (
														<span
															style={{
																color: theme.TA_GREEN,
																marginLeft: '20px',
																display: 'inline-block'
															}}
														>
															NA
														</span>
													)}
												</TitledValue>

												<TitledValue
													desc={
														<span className="d-flex align-items-center">
															<UserOutlined />{' '}
															<span className="ms-2">Contact Person:</span>
														</span>
													}
													layout="vertical"
													display="block"
													marginBottom={12}
												>
													<span style={{ marginLeft: '20px' }}>
														{`${company.contact.firstName} ${company.contact.lastName} (${company.contact.title})`}
													</span>
												</TitledValue>

												<TitledValue
													desc={
														<span className="d-flex align-items-center">
															<PhoneOutlined />{' '}
															<span className="ms-2">Contact Phone:</span>
														</span>
													}
													layout="vertical"
													display="block"
													marginBottom={12}
												>
													{company.contact.phoneNumber ? (
														<span style={{ marginLeft: '20px' }}>
															{company.contact.phoneNumber}
														</span>
													) : (
														<span
															style={{
																color: theme.TA_GREEN,
																marginLeft: '20px',
																display: 'inline-block'
															}}
														>
															NA
														</span>
													)}
												</TitledValue>

												<TitledValue
													desc={
														<span className="d-flex align-items-center">
															<MailOutlined />{' '}
															<span className="ms-2">Contact Email:</span>
														</span>
													}
													layout="vertical"
													display="block"
													marginBottom={12}
												>
													{company.contact.email ? (
														<span
															style={{
																marginLeft: '20px',
																wordBreak: 'break-all',
																display: 'inline-block'
															}}
														>
															{company.contact.email}
														</span>
													) : (
														<span
															style={{
																color: theme.TA_GREEN,
																marginLeft: '20px',
																display: 'inline-block'
															}}
														>
															NA
														</span>
													)}
												</TitledValue>
											</div>
										</Fragment>
									)}
								</Skeleton>
							</ContentBox>
						)}
					</Col>
				</Row>
			</Content>
		</Content>
	);
};
